import { useState, useEffect, useCallback, useMemo } from "react";
import { useTheme } from "../context/ThemeContext";
import { useLanguage, getLang, getLocale } from "../lang/useLanguage";

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:8001" : `${window.location.protocol}//${window.location.hostname}/api`;
const token = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });
const fmt = (d) => d ? new Date(d).toLocaleDateString(getLocale(), { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
const fmtDT = (d) => d ? new Date(d).toLocaleString(getLocale(), { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const money = (v) => v != null ? `${Number(v).toLocaleString("tr-TR")} ₺` : "—";

const CATEGORY_COLORS = [
  "#60a5fa","#00ff9d","#fbbf24","#f87171","#a78bfa",
  "#34d399","#fb923c","#e879f9","#38bdf8","#4ade80",
];

const getCategoryIcons = () => {
  const l = getLang(); const s = l.stockPage || {};
  return {
    [s.catRawMaterial]:     "⚙️",
    [s.catConsumable]: "🔧",
    [s.catSemiFinished]:    "🔩",
    [s.catSparePart]:  "🛠️",
    [s.catElectronic]:   "⚡",
    [s.catChemical]:     "🧪",
    [s.catPackaging]:      "📦",
  };
};

const getCategoryBadgeColors = () => {
  const l = getLang(); const s = l.stockPage || {};
  return {
    [s.catRawMaterial]:     { color: "#60a5fa", bg: "#60a5fa18" },
    [s.catConsumable]: { color: "#f97316", bg: "#f9731618" },
    [s.catSemiFinished]:    { color: "#a78bfa", bg: "#a78bfa18" },
    [s.catSparePart]:  { color: "#fbbf24", bg: "#fbbf2418" },
    [s.catElectronic]:   { color: "#00ff9d", bg: "#00ff9d18" },
    [s.catChemical]:     { color: "#f87171", bg: "#f8717118" },
    [s.catPackaging]:      { color: "#34d399", bg: "#34d39918" },
  };
};

const stockStatus = (item) => {
  if (!item.min_stock) return "ok";
  if (item.current_stock <= 0) return "empty";
  if (item.current_stock <= item.min_stock) return "low";
  if (item.current_stock <= item.min_stock * 1.5) return "warning";
  return "ok";
};

const getStockStatusCfg = (t) => {
  const l = getLang(); const s = l.stockPage || {};
  return {
    empty:   { label: s.statusEmpty, color: t.accent.red,    bg: t.accent.redBg },
    low:     { label: s.statusLow,  color: t.accent.red,    bg: t.accent.redBg },
    warning: { label: s.statusWarning,      color: t.accent.yellow, bg: t.accent.yellowBg },
    ok:      { label: s.statusOk, color: t.accent.green,  bg: t.accent.greenBg },
  };
};


 const StockBadge = ({ item, t }) => {
  const s = stockStatus(item);
  const cfg = getStockStatusCfg(t)[s];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}44`,
      letterSpacing: "0.04em", textTransform: "uppercase",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.color }} />
      {cfg.label}
    </span>
  );
};

// ─── STOK HAREKETİ MODAL ─────────────────────────────────────────────────
const MovementModal = ({ item, type, onClose, onSaved, t }) => {
  const { t: l } = useLanguage();
  const s = l.stockPage || {};
  const [form, setForm] = useState({ quantity: "", notes: "", supplier_id: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const typeLabels = {
    receipt:  { label: s.movReceipt,         icon: "📥", color: t.accent.green },
    issue:    { label: s.movIssue,         icon: "📤", color: t.accent.red },
    transfer: { label: s.movTransfer,           icon: "🔄", color: t.accent.blue },
    adjust:   { label: s.movAdjust,     icon: "⚖️", color: t.accent.yellow },
  };
  const cfg = typeLabels[type] || typeLabels.receipt;

  const handleSave = async () => {
    if (!form.quantity || Number(form.quantity) <= 0) { setError(s.errValidQty); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/stock/movement`, {
        method: "POST", headers: headers(),
        body: JSON.stringify({ stock_id: item.id, type, ...form, quantity: Number(form.quantity) }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || (s.errGeneric));
      onSaved();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const inputStyle = { width: "100%", padding: "10px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1200, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 440, boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: t.bg.header, borderRadius: "20px 20px 0 0" }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: t.text.primary }}>{cfg.icon} {cfg.label}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>
        <div style={{ padding: 24 }}>
          <div style={{ background: t.bg.tag, borderRadius: 10, padding: "12px 16px", marginBottom: 20, border: `1px solid ${t.border.subtle}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary }}>{item.name}</div>
            <div style={{ fontSize: 12, color: t.text.muted }}>{s.currentStock}: <strong style={{ color: cfg.color }}>{item.current_stock} {item.unit}</strong></div>
          </div>
          {Number(item.reserved_quantity) > 0 && (
            <div style={{ marginTop: 12, padding: "10px 14px", background: t.accent.yellowBg, border: `1px solid ${t.accent.yellow}44`, borderRadius: 10, marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: t.accent.yellow, fontWeight: 600 }}>🔒 {s.reserved} ({s.openWO})</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: t.accent.yellow }}>{item.reserved_quantity} {item.unit}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 12, color: t.text.muted, fontWeight: 600 }}>✅ {s.available}</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: Number(item.available_qty ?? item.available_quantity) > 0 ? "#10b981" : "#f87171" }}>
                  {item.available_qty ?? item.available_quantity ?? 0} {item.unit}
                </span>
              </div>
            </div>
          )}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{s.quantity} *</label>
            <input type="number" value={form.quantity} onChange={e => setForm(p => ({ ...p, quantity: e.target.value }))}
              placeholder={`${item.unit || (s.pcs)} ${s.inUnit}`} style={inputStyle} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{s.notes}</label>
            <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              rows={2} style={{ ...inputStyle, resize: "vertical" }} />
          </div>
          {error && <div style={{ padding: "10px 14px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 8, color: t.accent.red, fontSize: 13, marginBottom: 12 }}>{error}</div>}
        </div>
        <div style={{ padding: "16px 24px", borderTop: `1px solid ${t.border.default}`, display: "flex", justifyContent: "flex-end", gap: 10, background: t.bg.header, borderRadius: "0 0 20px 20px" }}>
          <button onClick={onClose} style={{ padding: "9px 18px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: "none", color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>{s.cancel}</button>
          <button onClick={handleSave} disabled={loading} style={{ padding: "9px 20px", borderRadius: 8, border: "none", background: loading ? t.bg.tag : cfg.color, color: "#fff", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 700 }}>
            {loading ? (s.saving) : cfg.label}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── STOK DETAY MODAL ────────────────────────────────────────────────────
const StockDetailModal = ({ item, onClose, onEdit, t, themeName }) => {
  const { t: l } = useLanguage();
  const s = l.stockPage || {};
  const [tab, setTab] = useState("overview");
  const [movements, setMovements] = useState([]);
  const [movType, setMovType] = useState(null);

  useEffect(() => {
    const fetch_ = async () => {
      try {
        const res = await fetch(`${API}/stock/${item.id}/movements?limit=20`, { headers: headers() });
        if (res.ok) setMovements(await res.json());
      } catch {}
    };
    fetch_();
  }, [item.id]);

  const st = stockStatus(item);
  const sCfg = getStockStatusCfg(t)[st];
  const pct = item.max_stock > 0 ? Math.min(100, (item.current_stock / item.max_stock) * 100) : 0;

  const tabs = [
    { id: "overview",  label: s.tabOverview },
    { id: "movements", label: s.tabMovements },
    { id: "reorder",   label: s.tabReorder },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 780, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>

        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: t.bg.header }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: sCfg.bg, border: `1px solid ${sCfg.color}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>📦</div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>{item.name}</div>
              <div style={{ color: t.text.muted, fontSize: 12, marginTop: 2 }}>{item.stock_code || "—"} · {item.category || "—"}</div>
            </div>
            <StockBadge item={item} t={t} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setMovType("receipt")} style={{ padding: "7px 12px", borderRadius: 8, border: `1px solid ${t.accent.green}44`, background: t.accent.greenBg, color: t.accent.green, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>📥 {s.movReceipt}</button>
            <button onClick={() => setMovType("issue")}   style={{ padding: "7px 12px", borderRadius: 8, border: `1px solid ${t.accent.red}44`,   background: t.accent.redBg,   color: t.accent.red,   cursor: "pointer", fontSize: 12, fontWeight: 600 }}>📤 {s.movIssue}</button>
            <button onClick={() => onEdit(item)} style={{ padding: "7px 12px", borderRadius: 8, border: `1px solid ${t.accent.blueBorder}`, background: t.accent.blueBg, color: t.accent.blue, cursor: "pointer", fontSize: 12 }}>✏️</button>
            <button onClick={onClose} style={{ padding: "7px 12px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 12 }}>✕</button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 2, padding: "0 28px", borderBottom: `1px solid ${t.border.divider}`, background: t.bg.header }}>
          {tabs.map(tb => (
            <button key={tb.id} onClick={() => setTab(tb.id)} style={{ padding: "11px 14px", background: "none", border: "none", cursor: "pointer", color: tab === tb.id ? t.accent.blue : t.text.muted, fontSize: 13, fontWeight: 600, borderBottom: `2px solid ${tab === tb.id ? t.accent.blue : "transparent"}`, transition: "all 0.2s" }}>{tb.label}</button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 28, background: t.bg.modal }}>

          {tab === "overview" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.stockLevel}</div>
                  <span style={{ fontSize: 22, fontWeight: 800, color: sCfg.color }}>{item.current_stock} <span style={{ fontSize: 13, color: t.text.muted }}>{item.unit}</span></span>
                </div>
                <div style={{ height: 10, background: t.bg.tag, borderRadius: 5, overflow: "hidden", marginBottom: 10 }}>
                  <div style={{ height: "100%", width: `${pct}%`, borderRadius: 5, background: st === "ok" ? t.accent.green : st === "warning" ? t.accent.yellow : t.accent.red, transition: "width 0.6s ease" }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: t.text.muted }}>
                  <span>Min: {item.min_stock || 0} {item.unit}</span>
                  <span>Maks: {item.max_stock || "—"} {item.unit}</span>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.06em" }}>📋 {s.materialInfo}</div>
                  {[
                    [s.stockCode,     item.stock_code],
                    [s.category,      item.category],
                    [s.location,  item.location || item.warehouse],
                    [s.unit_,          item.unit],
                    [s.unitPrice,   money(item.unit_price)],
                    [s.totalValue_,  money((item.current_stock || 0) * (item.unit_price || 0))],
                    [s.supplier_,     item.supplier_name],
                    [s.lastReceipt,     fmt(item.last_receipt)],
                    [s.lastIssue,     fmt(item.last_issue)],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${t.border.subtle}`, fontSize: 13 }}>
                      <span style={{ color: t.text.label }}>{k}</span>
                      <span style={{ color: t.text.primary, fontWeight: 500 }}>{v || "—"}</span>
                    </div>
                  ))}
                </div>

                <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.06em" }}>📊 {s.planningParams}</div>
                  {[
                    [s.reorderPoint, `${item.reorder_point || "—"} ${item.unit || ""}`],
                    [s.eoq,  `${item.eoq || "—"} ${item.unit || ""}`],
                    [s.safetyStock,          `${item.safety_stock || "—"} ${item.unit || ""}`],
                    [s.leadTime,            item.lead_time_days ? `${item.lead_time_days} ${s.days}` : "—"],
                    [s.batchTracking,              item.batch_tracking  ? (s.active) : (s.passive)],
                    [s.serialTracking,          item.serial_tracking ? (s.active) : (s.passive)],
                    [s.qualityCheck,         item.quality_check   ? (s.required) : (s.notRequired)],
                    [s.fifoLifo,             item.valuation_method || "FIFO"],
                    [s.abcClass,              item.abc_class || "—"],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${t.border.subtle}`, fontSize: 13 }}>
                      <span style={{ color: t.text.label }}>{k}</span>
                      <span style={{ color: t.text.primary, fontWeight: 500 }}>{v || "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "movements" && (
            <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: t.bg.theadRow }}>
                    {[s.date_, s.type_, s.qty, s.afterStock, s.notes, s.user].map(h => (
                      <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {movements.length === 0
                    ? <tr><td colSpan={6} style={{ textAlign: "center", color: t.text.placeholder, padding: 40 }}>{s.noMovements}</td></tr>
                    : movements.map((m, i) => {
                      const isIn = ["receipt", "return", "adjust_up"].includes(m.type);
                      return (
                        <tr key={i} style={{ borderTop: `1px solid ${t.border.subtle}` }}>
                          <td style={{ padding: "10px 14px", fontSize: 12, color: t.text.secondary }}>{fmtDT(m.created_at)}</td>
                          <td style={{ padding: "10px 14px" }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: isIn ? t.accent.green : t.accent.red }}>
                              {isIn ? "📥" : "📤"} {m.type_label || m.type}
                            </span>
                          </td>
                          <td style={{ padding: "10px 14px", fontWeight: 700, fontSize: 13, color: isIn ? t.accent.green : t.accent.red }}>
                            {isIn ? "+" : "-"}{m.quantity} {item.unit}
                          </td>
                          <td style={{ padding: "10px 14px", fontSize: 13, color: t.text.primary }}>{m.stock_after ?? "—"} {item.unit}</td>
                          <td style={{ padding: "10px 14px", fontSize: 12, color: t.text.muted }}>{m.notes || "—"}</td>
                          <td style={{ padding: "10px 14px", fontSize: 12, color: t.text.muted }}>{m.username || "—"}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}

          {tab === "reorder" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 16, textTransform: "uppercase", letterSpacing: "0.06em" }}>🔁 {s.autoPurchaseSuggestion}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
                  {[
                    { label: s.currentStock,            value: `${item.current_stock} ${item.unit}`, color: sCfg.color },
                    { label: s.reorderPoint,value: `${item.reorder_point || "—"} ${item.unit || ""}`, color: t.accent.yellow },
                    { label: s.suggestedOrder,       value: `${item.eoq || item.min_stock || "—"} ${item.unit || ""}`, color: t.accent.blue },
                  ].map(({ label, value, color }) => (
                    <div key={label} style={{ background: t.bg.tag, borderRadius: 10, padding: "14px 16px", border: `1px solid ${t.border.subtle}` }}>
                      <div style={{ fontSize: 11, color: t.text.muted, marginBottom: 4, textTransform: "uppercase", fontWeight: 600 }}>{label}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color }}>{value}</div>
                    </div>
                  ))}
                </div>
                {item.current_stock <= (item.reorder_point || item.min_stock) && (
                  <div style={{ padding: "14px 18px", background: t.accent.yellowBg, border: `1px solid ${t.accent.yellow}44`, borderRadius: 10, color: t.accent.yellow, fontSize: 13, fontWeight: 600 }}>
                    ⚠️ {s.reorderAlert}
                  </div>
                )}
                {item.supplier_name && (
                  <div style={{ marginTop: 14, padding: "14px 18px", background: t.bg.tag, border: `1px solid ${t.border.subtle}`, borderRadius: 10 }}>
                    <div style={{ fontSize: 12, color: t.text.muted, marginBottom: 4 }}>{s.supplier_}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary }}>{item.supplier_name}</div>
                    <div style={{ fontSize: 12, color: t.text.muted, marginTop: 2 }}>
                      {s.leadTime}: {item.lead_time_days ? `${item.lead_time_days} ${s.days}` : "—"} · {s.lastPrice}: {money(item.last_purchase_price)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {movType && (
        <MovementModal item={item} type={movType} t={t}
          onClose={() => setMovType(null)}
          onSaved={() => { setMovType(null); onClose(); }} />
      )}
    </div>
  );
};

// ─── TEDARİKÇİ KARTI ─────────────────────────────────────────────────────
const SupplierCard = ({ supplier, onClick, t }) => {
  const { t: l } = useLanguage();
  const s = l.stockPage || {};
  const rating = supplier.rating || 0;
  const stars = "★".repeat(Math.round(rating)) + "☆".repeat(5 - Math.round(rating));
  return (
    <div onClick={() => onClick(supplier)} style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: 20, cursor: "pointer", transition: "all 0.2s", boxShadow: t.shadow.card }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = `${t.accent.blue}55`; }}
      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = t.border.card; }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: t.accent.blueBg, border: `1px solid ${t.accent.blueBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🏢</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary }}>{supplier.name}</div>
            <div style={{ fontSize: 12, color: t.text.muted }}>{supplier.category || (s.generalSupplier)}</div>
          </div>
        </div>
        <span style={{ color: "#fbbf24", fontSize: 14, letterSpacing: 1 }}>{stars}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {[
          { label: s.productCount,    value: supplier.product_count || 0 },
          { label: s.activeOrders,  value: supplier.active_orders || 0 },
          { label: s.leadTimeDays,   value: supplier.lead_time_days ? `${supplier.lead_time_days}${s.dayShort}` : "—" },
          { label: s.paymentDays,   value: supplier.payment_days   ? `${supplier.payment_days}${s.dayShort}`   : "—" },
        ].map(({ label, value }) => (
          <div key={label} style={{ background: t.bg.metricCard, borderRadius: 8, padding: "8px 10px", border: `1px solid ${t.border.subtle}` }}>
            <div style={{ fontSize: 9, color: t.text.muted, textTransform: "uppercase", marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary }}>{value}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${t.border.subtle}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 11, color: t.text.muted }}>📞 {supplier.phone || "—"}</span>
        <span style={{ fontSize: 11, color: supplier.is_active ? t.accent.green : t.accent.red, fontWeight: 600 }}>
          ● {supplier.is_active ? (s.active) : (s.passive)}
        </span>
      </div>
    </div>
  );
};

// ─── TEDARİKÇİ DETAY MODAL ───────────────────────────────────────────────
const SupplierDetailModal = ({ supplier, onClose, onEdit, t }) => {
  const { t: l } = useLanguage();
  const s = l.stockPage || {};
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    const fetch_ = async () => {
      try {
        const res = await fetch(`${API}/suppliers/${supplier.id}/orders?limit=10`, { headers: headers() });
        if (res.ok) setOrders(await res.json());
      } catch {}
    };
    fetch_();
  }, [supplier.id]);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 720, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: t.bg.header }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: t.accent.blueBg, border: `1px solid ${t.accent.blueBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🏢</div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>{supplier.name}</div>
              <div style={{ fontSize: 12, color: t.text.muted }}>{supplier.category || "—"} · {supplier.tax_no || "—"}</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => onEdit(supplier)} style={{ padding: "7px 14px", borderRadius: 8, border: `1px solid ${t.accent.blueBorder}`, background: t.accent.blueBg, color: t.accent.blue, cursor: "pointer", fontSize: 12 }}>✏️ {s.edit}</button>
            <button onClick={onClose} style={{ padding: "7px 14px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 12 }}>✕</button>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 28 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
            <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.06em" }}>📋 {s.contact}</div>
              {[
                [s.contactPerson,  supplier.contact_name],
                [s.phone_,  supplier.phone],
                [s.email_,  supplier.email],
                [s.address_,    supplier.address],
                [s.city_,    supplier.city],
                [s.taxNo, supplier.tax_no],
              ].map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${t.border.subtle}`, fontSize: 13 }}>
                  <span style={{ color: t.text.label }}>{k}</span>
                  <span style={{ color: t.text.primary, fontWeight: 500 }}>{v || "—"}</span>
                </div>
              ))}
            </div>
            <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.06em" }}>📊 {s.performance}</div>
              {[
                [s.rating_,             `${"★".repeat(Math.round(supplier.rating || 0))} (${supplier.rating || 0}/5)`],
                [s.deliverySuccess,supplier.delivery_rate  ? `${supplier.delivery_rate}%`     : "—"],
                [s.qualityScore,     supplier.quality_score  ? `${supplier.quality_score}/100`  : "—"],
                [s.leadTimeDays,     supplier.lead_time_days ? `${supplier.lead_time_days} ${s.days}` : "—"],
                [s.paymentDays,     supplier.payment_days   ? `${supplier.payment_days} ${s.days}`   : "—"],
                [s.totalPurchase,      money(supplier.total_purchase)],
                [s.lastOrder,         fmt(supplier.last_order_date)],
                [s.contractEnd,  fmt(supplier.contract_end)],
              ].map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${t.border.subtle}`, fontSize: 13 }}>
                  <span style={{ color: t.text.label }}>{k}</span>
                  <span style={{ color: t.text.primary, fontWeight: 500 }}>{v || "—"}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, overflow: "hidden" }}>
            <div style={{ padding: "14px 20px", borderBottom: `1px solid ${t.border.default}`, fontSize: 13, fontWeight: 700, color: t.text.primary }}>{s.recentOrders}</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: t.bg.theadRow }}>
                  {[s.date_, s.material, s.qty, s.amount, s.statusLabel].map(h => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.length === 0
                  ? <tr><td colSpan={5} style={{ textAlign: "center", color: t.text.placeholder, padding: 32 }}>{s.noOrders}</td></tr>
                  : orders.map((o, i) => (
                    <tr key={i} style={{ borderTop: `1px solid ${t.border.subtle}` }}>
                      <td style={{ padding: "10px 14px", fontSize: 12, color: t.text.muted }}>{fmt(o.order_date || o.created_at)}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: t.text.primary }}>{o.material_name || o.product_name}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: t.text.secondary }}>{o.quantity} {o.unit}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: t.accent.blue, fontWeight: 600 }}>{money(o.total_price)}</td>
                      <td style={{ padding: "10px 14px" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, color: o.status === "delivered" ? t.accent.green : t.accent.yellow, background: o.status === "delivered" ? t.accent.greenBg : t.accent.yellowBg }}>
                          {o.status === "delivered" ? (s.delivered) : (s.waiting)}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── STOK / TEDARİKÇİ FORM MODAL ─────────────────────────────────────────
const FormModal = ({ type, data, onClose, onSaved, t }) => {
  const { t: l } = useLanguage();
  const s = l.stockPage || {};
  const isEdit = !!data;
  const isStock = type === "stock";

  const stockFields = [
    { label: `${s.materialName} *`, k: "name" },
    { label: s.stockCode,          k: "stock_code" },
    { label: s.category,            k: "category" },
    { label: s.currentStock,       k: "current_stock", type: "number" },
    { label: s.unit_,               k: "unit", opts: [{ v: "adet", l: s.pcs }, { v: "kg", l: "KG" }, { v: "lt", l: "LT" }, { v: "mt", l: "MT" }, { v: "paket", l: s.package }] },
    { label: s.minStock,           k: "min_stock",   type: "number" },
    { label: s.maxStock,          k: "max_stock",   type: "number" },
    { label: s.reorderPoint, k: "reorder_point", type: "number" },
    { label: s.safetyStock,     k: "safety_stock", type: "number" },
    { label: s.eoq,                 k: "eoq",         type: "number" },
    { label: s.unitPriceTl,    k: "unit_price",  type: "number" },
    { label: s.location,        k: "location" },
    { label: s.abcClass,          k: "abc_class", opts: [{ v: "A", l: `A - ${s.abcCritical}` }, { v: "B", l: `B - ${s.abcImportant}` }, { v: "C", l: `C - ${s.abcLow}` }] },
    { label: s.fifoLifo,           k: "valuation_method", opts: [{ v: "FIFO", l: "FIFO" }, { v: "LIFO", l: "LIFO" }] },
  ];

  const supplierFields = [
    { label: `${s.supplierName} *`, k: "name" },
    { label: s.category,           k: "category" },
    { label: s.contactPerson,       k: "contact_name" },
    { label: s.phone_,            k: "phone" },
    { label: s.email_,            k: "email" },
    { label: s.address_,              k: "address" },
    { label: s.city_,              k: "city" },
    { label: s.taxNo,           k: "tax_no" },
    { label: s.leadTimeDaysLabel, k: "lead_time_days", type: "number" },
    { label: s.paymentDaysLabel, k: "payment_days",   type: "number" },
    { label: s.rating1to5,         k: "rating",         type: "number" },
    { label: s.contractEnd,     k: "contract_end",   type: "date" },
  ];

  const fields = isStock ? stockFields : supplierFields;
  const initForm = fields.reduce((a, f) => ({ ...a, [f.k]: data?.[f.k] || "" }), {});
  const [form, setForm] = useState(initForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name) { setError(s.errNameRequired); return; }
    setLoading(true); setError("");
    const endpoint = isStock ? "/stock" : "/suppliers";
    try {
      const url = isEdit ? `${API}${endpoint}/${data.id}` : `${API}${endpoint}`;
      const res = await fetch(url, { method: isEdit ? "PUT" : "POST", headers: headers(), body: JSON.stringify(form) });
      if (!res.ok) throw new Error((await res.json()).detail || (s.errGeneric));
      onSaved();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const inputStyle = { width: "100%", padding: "10px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1100, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 680, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: t.bg.header }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>
            {isEdit ? `✏️ ${s.edit}` : isStock ? `➕ ${s.newMaterial}` : `➕ ${s.newSupplier}`}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 28 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {fields.map(f => (
              <div key={f.k}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{f.label}</label>
                {f.opts ? (
                  <select value={form[f.k]} onChange={e => set(f.k, e.target.value)} style={inputStyle}>
                    {f.opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                ) : (
                  <input type={f.type || "text"} value={form[f.k]} onChange={e => set(f.k, e.target.value)} style={inputStyle} />
                )}
              </div>
            ))}
          </div>
          {error && <div style={{ marginTop: 16, padding: "10px 16px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 8, color: t.accent.red, fontSize: 13 }}>{error}</div>}
        </div>
        <div style={{ padding: "16px 28px", borderTop: `1px solid ${t.border.default}`, display: "flex", justifyContent: "flex-end", gap: 12, background: t.bg.header }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: "none", color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>{s.cancel}</button>
          <button onClick={handleSave} disabled={loading} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: loading ? t.bg.tag : "linear-gradient(135deg,#3b82f6,#6366f1)", color: "#fff", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 600, boxShadow: loading ? "none" : t.shadow.btn }}>
            {loading ? (s.saving) : isEdit ? (s.update) : (s.add)}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── SARF TALEP BİLEŞENİ ────────────────────────────────────────────────
function StockRequests({ requests, setRequests, reqForm, setReqForm, reqLoading, setReqLoading,
  showReqForm, setShowReqForm, reqSuggestions, setReqSuggestions, showReqSuggest, setShowReqSuggest,
  currentUser, t, API, headers }) {

  const { t: l } = useLanguage();
  const s = l.stockPage || {};
  const [filter, setFilter] = useState("all");
  const role = (() => { try { return JSON.parse(atob(localStorage.getItem("token").split(".")[1])).role; } catch { return ""; } })();
  const isWarehouse = ["admin","manager","warehouse"].includes(role);

  const fetchRequests = useCallback(async () => {
    try {
      const r = await fetch(`${API}/stock-requests`, { headers: headers() });
      if (r.ok) setRequests(await r.json());
    } catch {}
  }, [API, headers, setRequests]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleSubmit = async () => {
    if (!reqForm.material_name || !reqForm.quantity) return;
    setReqLoading(true);
    try {
      const r = await fetch(`${API}/stock-requests`, { method: "POST", headers: headers(), body: JSON.stringify(reqForm) });
      if (r.ok) {
        setReqForm({ material_name: "", quantity: "", unit: s.pcs, department: "", machine: "", reason: "" });
        setShowReqForm(false);
        fetchRequests();
      } else { const e = await r.json(); alert(e.detail || (s.errGeneric)); }
    } catch(e) { alert(s.connectionError); }
    setReqLoading(false);
  };

  const handleApprove = async (id, action, notes="") => {
    try {
      const r = await fetch(`${API}/stock-requests/${id}/approve`, { method: "PATCH", headers: headers(), body: JSON.stringify({ action, notes }) });
      if (r.ok) fetchRequests();
      else { const e = await r.json(); alert(e.detail || (s.errGeneric)); }
    } catch { alert(s.connectionError); }
  };

  const filtered = requests.filter(r => {
    const st = String(r.status || "").toLowerCase();
    if (filter === "all") return true;
    if (filter === "pending") return ["pending","requested","waiting"].includes(st);
    if (filter === "approved") return ["approved","delivered","received","completed"].includes(st);
    if (filter === "rejected") return ["rejected","cancelled"].includes(st);
    return st === filter;
  });
  const pending = requests.filter(r => ["pending","requested","waiting"].includes(String(r.status||"").toLowerCase())).length;
  const delivered = requests.filter(r => ["approved","delivered","received","completed"].includes(String(r.status||"").toLowerCase())).length;

  const statusCfg = {
    pending:  { label: s.reqPending,   color: "#f59e0b", bg: "#f59e0b18" },
    approved: { label: s.requestDelivered,  color: "#22c55e", bg: "#22c55e18" },
    delivered:{ label: s.requestDelivered,  color: "#22c55e", bg: "#22c55e18" },
    received: { label: s.requestDelivered,  color: "#22c55e", bg: "#22c55e18" },
    completed:{ label: s.requestDelivered,  color: "#22c55e", bg: "#22c55e18" },
    rejected: { label: s.reqRejected, color: "#ef4444", bg: "#ef444418" },
  };

  const IS = { width:"100%", padding:"9px 12px", background:t.bg.input, border:`1px solid ${t.border.input}`, borderRadius:8, color:t.text.primary, fontSize:13, outline:"none", boxSizing:"border-box" };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
        {[
          [s.reqTotal,    requests.length, "#60a5fa"],
          [s.reqPending,  pending, "#f59e0b"],
          [s.requestDeliveredCount, delivered, "#22c55e"],
          [s.reqRejected,requests.filter(r=>r.status==="rejected").length, "#ef4444"],
        ].map(([label, val, color]) => (
          <div key={label} style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:12, padding:"16px 20px", boxShadow:t.shadow.card }}>
            <div style={{ fontSize:11, color:t.text.muted, fontWeight:700, textTransform:"uppercase", marginBottom:6 }}>{label}</div>
            <div style={{ fontSize:24, fontWeight:800, color }}>{val}</div>
          </div>
        ))}
      </div>

      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ display:"flex", gap:4, background:t.bg.toggle, border:`1px solid ${t.border.input}`, borderRadius:10, padding:4 }}>
          {[
            ["all",      s.reqAll],
            ["pending",  s.reqPending],
            ["approved", s.requestDeliveredCount],
            ["rejected", s.reqRejected],
          ].map(([v, label]) => (
            <button key={v} onClick={()=>setFilter(v)} style={{ padding:"7px 14px", borderRadius:7, border:"none", cursor:"pointer", background: filter===v ? t.bg.card : "none", color: filter===v ? t.text.primary : t.text.muted, fontSize:12, fontWeight:600, boxShadow: filter===v ? t.shadow.card : "none" }}>
              {label}{v==="pending"&&pending>0?` (${pending})`:""}</button>
          ))}
        </div>
        <button onClick={()=>setShowReqForm(!showReqForm)} style={{ padding:"9px 18px", borderRadius:10, border:"none", background:"linear-gradient(135deg,#3b82f6,#1d4ed8)", color:"#fff", cursor:"pointer", fontSize:13, fontWeight:700 }}>
          + {s.requestConsumable}
        </button>
      </div>

      {showReqForm && (
        <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:20, boxShadow:t.shadow.card }}>
          <div style={{ fontSize:14, fontWeight:700, color:t.text.primary, marginBottom:16 }}>📋 {s.newRequest}</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div style={{ position:"relative" }}>
              <label style={{ display:"block", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", marginBottom:6 }}>{s.materialName} *</label>
              <input value={reqForm.material_name} onChange={async e => {
                const val = e.target.value;
                setReqForm(p=>({...p, material_name:val, unit: s.pcs}));
                if (val.length >= 2) {
                  try {
                    const r = await fetch(`${API}/stock?search=${encodeURIComponent(val)}`, { headers: headers() });
                    const d = await r.json();
                    setReqSuggestions(Array.isArray(d) ? d.slice(0,8) : []);
                    setShowReqSuggest(true);
                  } catch { setReqSuggestions([]); }
                } else { setShowReqSuggest(false); }
              }} onBlur={()=>setTimeout(()=>setShowReqSuggest(false),200)}
              style={IS} placeholder={s.reqMaterialPlaceholder} autoComplete="off" />
              {showReqSuggest && reqSuggestions.length > 0 && (
                <div style={{ position:"absolute", top:"100%", left:0, right:0, background:"#1e2a3a", border:"1px solid #334155", borderRadius:8, zIndex:100, maxHeight:200, overflowY:"auto", boxShadow:"0 4px 12px rgba(0,0,0,0.4)" }}>
                  {reqSuggestions.map((sg,i) => (
                    <div key={i} onMouseDown={()=>{ setReqForm(p=>({...p, material_name:sg.name||sg.material_name, unit:sg.unit||(s.pcs)})); setShowReqSuggest(false); }}
                      style={{ padding:"8px 12px", cursor:"pointer", fontSize:13, color:"#e2e8f0", borderBottom:"1px solid #334155", display:"flex", justifyContent:"space-between" }}>
                      <span>{sg.name||sg.material_name}</span>
                      <span style={{ color:"#60a5fa", fontSize:11 }}>{s.current}: {sg.current_stock||sg.current_quantity||0} {sg.unit}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label style={{ display:"block", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", marginBottom:6 }}>{s.quantity} *</label>
              <div style={{ display:"flex", gap:8 }}>
                <input type="number" value={reqForm.quantity} onChange={e=>setReqForm(p=>({...p,quantity:e.target.value}))} style={{...IS,flex:1}} min="0" step="0.01" />
                <input value={reqForm.unit} onChange={e=>setReqForm(p=>({...p,unit:e.target.value}))} style={{...IS,width:80}} placeholder={s.pcs} />
              </div>
            </div>
            <div>
              <label style={{ display:"block", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", marginBottom:6 }}>{s.department}</label>
              <input value={reqForm.department} onChange={e=>setReqForm(p=>({...p,department:e.target.value}))} style={IS} placeholder={s.deptPlaceholder} />
            </div>
            <div>
              <label style={{ display:"block", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", marginBottom:6 }}>{s.machine_}</label>
              <input value={reqForm.machine} onChange={e=>setReqForm(p=>({...p,machine:e.target.value}))} style={IS} placeholder="CNC-01, LASER-01..." />
            </div>
            <div style={{ gridColumn:"1/-1" }}>
              <label style={{ display:"block", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", marginBottom:6 }}>{s.reqReason}</label>
              <input value={reqForm.reason} onChange={e=>setReqForm(p=>({...p,reason:e.target.value}))} style={IS} placeholder={s.reqReasonPlaceholder} />
            </div>
          </div>
          <div style={{ display:"flex", gap:10, marginTop:16, justifyContent:"flex-end" }}>
            <button onClick={()=>setShowReqForm(false)} style={{ padding:"9px 18px", borderRadius:8, border:`1px solid ${t.border.input}`, background:"none", color:t.text.secondary, cursor:"pointer" }}>{s.cancel}</button>
            <button onClick={handleSubmit} disabled={reqLoading} style={{ padding:"9px 20px", borderRadius:8, border:"none", background:"linear-gradient(135deg,#22c55e,#16a34a)", color:"#fff", cursor:"pointer", fontWeight:700 }}>
              {reqLoading ? "..." : `📋 ${s.sendRequest}`}
            </button>
          </div>
        </div>
      )}

      <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, overflow:"hidden", boxShadow:t.shadow.card }}>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ background:t.bg.theadRow }}>
              {["#", s.material, s.qty, s.department, s.machine_, s.requestedBy, s.reqReason, s.statusLabel, s.actions].map(h=>(
                <th key={h} style={{ padding:"11px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", whiteSpace:"nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={9} style={{ padding:40, textAlign:"center", color:t.text.placeholder }}>{s.noRequests}</td></tr>
            ) : filtered.map((r,i) => {
              const sc = statusCfg[r.status] || statusCfg.pending;
              return (
                <tr key={r.id} style={{ borderTop:`1px solid ${t.border.subtle}`, background: i%2===0 ? t.bg.rowAlt : t.bg.row }}>
                  <td style={{ padding:"11px 14px", fontSize:12, color:t.text.muted }}>#{r.id}</td>
                  <td style={{ padding:"11px 14px", fontSize:13, fontWeight:600, color:t.text.primary }}>{r.material_name}</td>
                  <td style={{ padding:"11px 14px", fontSize:13, color:t.accent.blue, fontWeight:700 }}>{r.quantity} {r.unit}</td>
                  <td style={{ padding:"11px 14px", fontSize:12, color:t.text.secondary }}>{r.department||"—"}</td>
                  <td style={{ padding:"11px 14px", fontSize:12, color:t.text.secondary }}>{r.machine||"—"}</td>
                  <td style={{ padding:"11px 14px", fontSize:12, color:t.text.secondary }}>{r.requested_by}</td>
                  <td style={{ padding:"11px 14px", fontSize:12, color:t.text.muted, maxWidth:150, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.reason||"—"}</td>
                  <td style={{ padding:"11px 14px" }}>
                    <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:20, color:sc.color, background:sc.bg }}>{sc.label}</span>
                  </td>
                  <td style={{ padding:"11px 14px" }}>
                    {r.status === "pending" && isWarehouse && (
                      <div style={{ display:"flex", gap:6 }}>
                        <button onClick={()=>handleApprove(r.id,"approve")} style={{ padding:"5px 10px", borderRadius:6, border:"none", background:"#22c55e", color:"#fff", cursor:"pointer", fontSize:11, fontWeight:700 }}>✓ {s.approve}</button>
                        <button onClick={()=>{ const n=prompt(s.rejectReasonPrompt); if(n!==null) handleApprove(r.id,"reject",n); }} style={{ padding:"5px 10px", borderRadius:6, border:"none", background:"#ef4444", color:"#fff", cursor:"pointer", fontSize:11, fontWeight:700 }}>✕ {s.reject}</button>
                      </div>
                    )}
                    {r.status === "approved" && <span style={{ fontSize:11, color:"#22c55e" }}>✓ {r.approved_by}</span>}
                    {r.status === "rejected" && <span style={{ fontSize:11, color:"#ef4444" }}>✕ {r.notes||r.approved_by}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── ANA SAYFA ────────────────────────────────────────────────────────────

const AssemblyMaterialsTab = ({ t, API, headers, currentUser }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/stock-requests`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setRequests(list.filter(r => (r.request_type || '') === 'assembly'));
      }
    } catch {}
    setLoading(false);
  }, [API, headers]);

  useEffect(() => { load(); }, [load]);

  const { t: l } = useLanguage();
  const s = l.stockPage || {};

  const approve = async (reqId) => {
    setProcessing(p => ({ ...p, [reqId]: true }));
    try {
      const res = await fetch(`${API}/stock-requests/${reqId}/approve`, {
        method: "PATCH",
        headers: headers(),
        body: JSON.stringify({ action: "approve", notes: s.assemblyApproveNote })
      });
      const d = await res.json();
      if (res.ok) { setMsg({ type: "success", text: s.assemblyDeliveredSuccess }); load(); }
      else setMsg({ type: "error", text: d.detail || s.errGeneric });
    } catch { setMsg({ type: "error", text: s.connectionError }); }
    setProcessing(p => ({ ...p, [reqId]: false }));
    setTimeout(() => setMsg(null), 3000);
  };

  const reject = async (reqId) => {
    const notes = prompt(s.assemblyRejectReasonPrompt);
    if (notes === null) return;
    setProcessing(p => ({ ...p, [reqId]: true }));
    try {
      const res = await fetch(`${API}/stock-requests/${reqId}/approve`, {
        method: "PATCH",
        headers: headers(),
        body: JSON.stringify({ action: "reject", notes })
      });
      const d = await res.json();
      if (res.ok) { setMsg({ type: "success", text: s.assemblyRejectedSuccess }); load(); }
      else setMsg({ type: "error", text: d.detail || s.errGeneric });
    } catch { setMsg({ type: "error", text: s.connectionError }); }
    setProcessing(p => ({ ...p, [reqId]: false }));
    setTimeout(() => setMsg(null), 3000);
  };

  const statusCfg = {
    pending:   { label: s.assemblyStatusWaiting, color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
    requested: { label: s.assemblyStatusWaiting, color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
    waiting:   { label: s.assemblyStatusWaiting, color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },

    approved:  { label: s.assemblyStatusDelivered, color: "#10b981", bg: "rgba(16,185,129,0.12)" },
    delivered: { label: s.assemblyStatusDelivered, color: "#10b981", bg: "rgba(16,185,129,0.12)" },
    received:  { label: s.assemblyStatusDelivered, color: "#10b981", bg: "rgba(16,185,129,0.12)" },
    completed: { label: s.assemblyStatusDelivered, color: "#10b981", bg: "rgba(16,185,129,0.12)" },

    rejected:  { label: s.assemblyStatusRejected, color: "#ef4444", bg: "rgba(239,68,68,0.12)" },
    cancelled: { label: s.assemblyStatusRejected, color: "#ef4444", bg: "rgba(239,68,68,0.12)" },
  };

  if (loading) return <div style={{ textAlign: "center", padding: 40, color: t.text.muted }}>⏳ {s.loading}</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: t.text.primary }}>{s.assemblyRequestsTitle}</div>
        <button onClick={load} style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>🔄 {s.refresh}</button>
      </div>

      {msg && (
        <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 600,
          background: msg.type === "success" ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.12)",
          color: msg.type === "success" ? "#10b981" : "#ef4444",
          border: `1px solid ${msg.type === "success" ? "#10b98133" : "#ef444433"}` }}>
          {msg.text}
        </div>
      )}

      {requests.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: t.text.placeholder }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔧</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{s.assemblyNoPending}</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>{s.assemblyNoPendingHint}</div>
        </div>
      ) : (
        <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: t.bg.theadRow }}>
                {[s.assemblyRequest, s.assemblyWorkOrder, s.assemblyMaterial, s.assemblyQuantity, s.assemblyRequestedBy, s.assemblyStock, s.assemblyStatus, s.assemblyAction].map(h => (
                  <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {requests.map((r, i) => {
                const sc = statusCfg[r.status] || statusCfg.pending;
                const stockOk = Number(r.stock_available || 0) >= Number(r.quantity || 0);
                return (
                  <tr key={r.id} style={{ borderTop: `1px solid ${t.border.subtle}`, background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                    <td style={{ padding: "11px 12px", fontSize: 12, color: t.accent.blue, fontWeight: 700 }}>MT-{String(r.id).padStart(5,"0")}</td>
                    <td style={{ padding: "11px 12px", fontSize: 12, color: "#a78bfa", fontWeight: 700 }}>{r.work_order_no || "—"}</td>
                    <td style={{ padding: "11px 12px" }}>
                      <div style={{ fontSize: 13, color: t.text.primary, fontWeight: 700 }}>{r.material_name}</div>
                      <div style={{ fontSize: 11, color: t.text.muted }}>{r.reason || s.assemblyDefaultReason}</div>
                    </td>
                    <td style={{ padding: "11px 12px", fontSize: 13, color: t.text.secondary, fontWeight: 700 }}>{r.quantity} {r.unit}</td>
                    <td style={{ padding: "11px 12px", fontSize: 12, color: t.text.secondary }}>{r.requested_by || "—"}</td>
                    <td style={{ padding: "11px 12px", fontSize: 12, color: stockOk ? "#10b981" : "#ef4444", fontWeight: 700 }}>
                      {stockOk ? s.assemblyInStock : s.assemblyOutOfStock} · {r.stock_available ?? 0} {r.unit}
                    </td>
                    <td style={{ padding: "11px 12px" }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, color: sc.color, background: sc.bg }}>{sc.label}</span>
                    </td>
                    <td style={{ padding: "11px 12px" }}>
                      {r.status === "pending" ? (
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            disabled={processing[r.id] || !stockOk}
                            onClick={() => approve(r.id)}
                            style={{
                              padding: "6px 12px", borderRadius: 7, border: "none",
                              background: !stockOk ? t.bg.tag : "linear-gradient(135deg,#059669,#10b981)",
                              color: !stockOk ? t.text.muted : "#fff",
                              cursor: !stockOk ? "not-allowed" : "pointer",
                              fontSize: 12, fontWeight: 700
                            }}>
                            {processing[r.id] ? "..." : !stockOk ? s.stockUnavailable : s.deliverAction}
                          </button>
                          <button
                            disabled={processing[r.id]}
                            onClick={() => reject(r.id)}
                            style={{ padding: "6px 10px", borderRadius: 7, border: "none", background: "#ef4444", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
                            Reddet
                          </button>
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: t.text.muted }}>{r.approved_by || r.notes || "—"}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
export default function Stock() {
  const { t, themeName } = useTheme();
  const { t: l } = useLanguage();
  const s = useMemo(() => l.stockPage || {}, [l.stockPage]);
  const currentUser = (() => { try { return JSON.parse(atob(localStorage.getItem("token").split(".")[1])); } catch { return {}; } })();
  const [mainTab, setMainTab] = useState(localStorage.getItem("tab_stock") || "raw");
  const [requests, setRequests] = useState([]);
  const [reqForm, setReqForm] = useState({ material_name: "", quantity: "", unit: s.pcs, department: "", machine: "", reason: "" });
  const [reqLoading, setReqLoading] = useState(false);
  const [showReqForm, setShowReqForm] = useState(false);
  const [reqSuggestions, setReqSuggestions] = useState([]);
  const [showReqSuggest, setShowReqSuggest] = useState(false);
  const [items, setItems] = useState([]);
  const [finishedItems, setFinishedItems] = useState([]);
  const [semiItems, setSemiItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [view, setView] = useState(localStorage.getItem("view_stock") || "grid");
  const [selected, setSelected] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [formType, setFormType] = useState(null);
  const [formData, setFormData] = useState(null);
  const [stats, setStats] = useState({});
  const [finishedStats, setFinishedStats] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        fetch(`${API}/stock?materials_only=true`, { headers: headers() }),
        fetch(`${API}/suppliers`, { headers: headers() }),
        fetch(`${API}/finished-goods`, { headers: headers() }),
      ]);
      if (r1.ok) {
        const data = await r1.json();
        const list = Array.isArray(data) ? data : data.items || [];
        setItems(list);
        const critical = list.filter(i => stockStatus(i) === "low" || stockStatus(i) === "empty").length;
        const totalValue = list.reduce((su, i) => su + ((i.current_stock || 0) * (i.unit_price || 0)), 0);
        setStats({ total: list.length, critical, totalValue });
      } else { throw new Error(s.errFetchStock); }
      if (r2.ok) { const data = await r2.json(); setSuppliers(Array.isArray(data) ? data : data.items || []); }
      if (r3.ok) {
        const data = await r3.json();
        const list = Array.isArray(data) ? data : data.items || [];
        const finished = list.filter(i => i.product_type !== "semi");
        const semi     = list.filter(i => i.product_type === "semi");
        setFinishedItems(finished);
        setSemiItems(semi);
        const readyToShip = finished.filter(i => i.status === "in_stock" && Number(i.reserved_quantity) === 0).length;
        const totalValue = finished.reduce((su, i) => su + ((i.quantity || 0) * (i.sale_price || i.unit_cost || 0)), 0);
        setFinishedStats({ total: finished.length, readyToShip, totalValue });
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [s]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const CATEGORY_ICONS = getCategoryIcons();
  const CATEGORY_BADGE_COLORS = getCategoryBadgeColors();
  const categories = [...new Set(items.map(i => i.category).filter(Boolean))];

  const filteredItems = items.filter(i => {
    const matchSearch = !search || (i.name || "").toLowerCase().includes(search.toLowerCase()) || (i.stock_code || "").toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCategory === "all" || i.category === filterCategory;
    const matchStatus = filterStatus === "all" || stockStatus(i) === filterStatus;
    return matchSearch && matchCat && matchStatus;
  });

  const filteredSuppliers = suppliers.filter(su => !search || (su.name || "").toLowerCase().includes(search.toLowerCase()));

  const selectStyle = { padding: "10px 14px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 10, color: t.text.primary, fontSize: 13, outline: "none", cursor: "pointer" };

  const getFinishedStatusInfo = (item) => {
    const isPendingWarehouse = item.status === "pending_warehouse";
    const isReserved = item.status === "reserved" || Number(item.reserved_quantity) > 0;
    const isInStock  = item.status === "in_stock";
    const isShipped  = item.status === "shipped";
    if (isPendingWarehouse) return { color: t.accent.yellow, label: `⏳ ${s.pendingWarehouse}` };
    if (isShipped)           return { color: t.accent.blue,   label: s.shipped };
    if (isReserved && isInStock) return { color: "#f59e0b",   label: s.reserved };
    if (isInStock)           return { color: t.accent.green,  label: s.ready };
    return { color: t.accent.purple, label: item.status || "—" };
  };

  return (
    <div style={{ minHeight: "100vh", background: t.bg.page, color: t.text.primary, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap'); @keyframes sf-spin { to{transform:rotate(360deg)} }`}</style>
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "28px 24px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: t.text.primary, margin: 0, letterSpacing: "-0.02em" }}>📦 {s.title}</h1>
            <p style={{ color: t.text.muted, margin: "6px 0 0", fontSize: 14 }}>{s.subtitle}</p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={fetchData} style={{ padding: "10px 16px", borderRadius: 10, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>🔄 {s.refresh}</button>
            {mainTab !== "requests" && mainTab !== "assembly" && (
              <button onClick={() => {
                let hdrs, rows, filename;
                if (mainTab === "raw") {
                  hdrs = [s.stockCode, s.materialName, s.category, s.qty, s.unit_, s.minStock, s.unitPrice, s.supplier_, s.statusLabel];
                  rows = (items||[]).map(i => [i.product_code||i.material_code||"—", i.product_name||i.material_name||i.name||"—", i.category||"—", i.quantity||i.stock_qty||0, i.unit||"—", i.min_stock||i.minimum_stock||0, i.unit_price||i.price||0, i.supplier||i.supplier_name||"—", i.status||"—"]);
                  filename = `raw_stock_${new Date().toISOString().slice(0,10)}.csv`;
                } else if (mainTab === "finished") {
                  hdrs = [s.productCode, s.productNameHeader, s.total_, s.reserved, s.available, s.workOrder, s.customer, s.statusLabel, s.source];
                  rows = (finishedItems||[]).map(i => [i.product_code||"—", i.product_name||"—", i.quantity||0, i.reserved_quantity||0, i.available_qty||0, i.order_no||"—", i.customer||"—", i.status||"—", i.source||"—"]);
                  filename = `finished_goods_${new Date().toISOString().slice(0,10)}.csv`;
                } else if (mainTab === "semi") {
                  hdrs = [s.productCode, s.productNameHeader, s.total_, s.statusLabel];
                  rows = (semiItems||[]).map(i => [i.product_code||"—", i.product_name||"—", i.quantity||0, i.status||"—"]);
                  filename = `semi_goods_${new Date().toISOString().slice(0,10)}.csv`;
                } else if (mainTab === "suppliers") {
                  hdrs = [s.supplierName, s.code, s.category, s.contact, s.email_, s.phone_, s.statusLabel];
                  rows = (suppliers||[]).map(su => [su.name||"—", su.code||su.supplier_code||"—", su.category||"—", su.contact_person||su.contact||"—", su.email||"—", su.phone||"—", su.status||"—"]);
                  filename = `suppliers_${new Date().toISOString().slice(0,10)}.csv`;
                } else { return; }
                const csv = [hdrs, ...rows].map(r => r.map(v => `"${String(v||"").replace(/"/g,'""')}"`).join(";")).join("\n");
                const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
                const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
              }} style={{ padding: "10px 16px", borderRadius: 10, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>📥 Excel</button>
            )}
            {mainTab !== "requests" && mainTab !== "assembly" && (
              <button onClick={() => {
                if (mainTab === "raw") { setFormType("stock"); setFormData(null); }
                else if (mainTab === "finished") { setFormType("finished"); setFormData(null); }
                else if (mainTab === "semi") { setFormType("finished"); setFormData(null); }
                else { setFormType("supplier"); setFormData(null); }
              }} style={{ padding: "10px 20px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#3b82f6,#6366f1)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, boxShadow: t.shadow.btn }}>
                + {mainTab === "raw" ? (s.addMaterial) : mainTab === "finished" ? (s.addProduct) : mainTab === "semi" ? (s.addSemi) : (s.addSupplier)}
              </button>
            )}
          </div>
        </div>

        {/* Ana Sekmeler */}
        <div style={{ display: "flex", gap: 4, marginBottom: 24, background: t.bg.toggle, border: `1px solid ${t.border.input}`, borderRadius: 12, padding: 4, width: "fit-content" }}>
          {[
            ["raw",       `🔩 ${s.tabRaw}`],
            ["finished",  `📦 ${s.tabFinished}`],
            ["semi",      `🔩 ${s.tabSemi}`],
            ["suppliers", `🏢 ${s.tabSuppliers}`],
            ["assembly",  `🔧 Montaj Talepleri`],
            ["requests",  `📋 ${s.tabRequests}`],
          ].map(([v, label]) => (
            <button key={v} onClick={() => { setMainTab(v); localStorage.setItem("tab_stock", v); setSearch(""); setFilterCategory("all"); setFilterStatus("all"); }} style={{ padding: "9px 20px", borderRadius: 9, border: "none", cursor: "pointer", background: mainTab === v ? (themeName === "dark" ? "rgba(255,255,255,0.12)" : "#fff") : "none", color: mainTab === v ? t.text.primary : t.text.muted, fontSize: 13, fontWeight: 600, boxShadow: mainTab === v ? t.shadow.card : "none", transition: "all 0.2s" }}>{label}</button>
          ))}
        </div>

        {/* HAMMADDE SEKMESİ */}
        {mainTab === "raw" && (
          <>
            <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
              {[
                { label: s.totalItems,  value: stats.total   || 0,    color: t.accent.blue,   icon: "📦" },
                { label: s.criticalStock,   value: stats.critical || 0,   color: t.accent.red,    icon: "⚠️" },
                { label: s.supplierCount,     value: suppliers.length,       color: t.accent.purple, icon: "🏢" },
                { label: s.totalValue_,  value: stats.totalValue ? `${(stats.totalValue / 1000).toFixed(0)}K ₺` : "0 ₺", color: t.accent.green, icon: "💰" },
              ].map(st => (
                <div key={st.label} style={{ flex: 1, minWidth: 140, background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: "16px 20px", display: "flex", alignItems: "center", gap: 14, boxShadow: t.shadow.card }}>
                  <span style={{ fontSize: 24 }}>{st.icon}</span>
                  <div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: st.color }}>{st.value}</div>
                    <div style={{ fontSize: 12, color: t.text.muted, fontWeight: 600 }}>{st.label}</div>
                  </div>
                </div>
              ))}
            </div>

            {stats.critical > 0 && (
              <div style={{ padding: "12px 20px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 12, marginBottom: 20, display: "flex", alignItems: "center", gap: 10, color: t.accent.red, fontSize: 13, fontWeight: 600 }}>
                ⚠️ {stats.critical} {s.criticalStockAlert}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
              <input placeholder={`🔍 ${s.searchMaterial}`} value={search} onChange={e => setSearch(e.target.value)} style={{ ...selectStyle, flex: 1, minWidth: 220 }} />
              {categories.length > 0 && (
                <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} style={selectStyle}>
                  <option value="all">{s.allCategories}</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              )}
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selectStyle}>
                <option value="all">{s.allStatuses}</option>
                <option value="empty">{s.statusEmpty}</option>
                <option value="low">{s.statusLow}</option>
                <option value="warning">{s.statusWarning}</option>
                <option value="ok">{s.statusOk}</option>
              </select>
              <div style={{ display: "flex", gap: 4, background: t.bg.toggle, border: `1px solid ${t.border.input}`, borderRadius: 10, padding: 4 }}>
                {[["grid","⊞"],["list","≡"]].map(([v, icon]) => (
                  <button key={v} onClick={() => { setView(v); localStorage.setItem("view_stock", v); }} style={{ padding: "6px 12px", borderRadius: 7, border: "none", cursor: "pointer", background: view === v ? t.bg.toggleActive : "none", color: view === v ? t.text.primary : t.text.muted, fontSize: 16 }}>{icon}</button>
                ))}
              </div>
            </div>

            {loading ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 12 }}>
                <div style={{ width: 28, height: 28, border: `2px solid ${t.border.default}`, borderTopColor: t.accent.blue, borderRadius: "50%", animation: "sf-spin 0.8s linear infinite" }} />
                <span style={{ color: t.text.muted }}>{s.loadingStock}</span>
              </div>
            ) : error ? (
              <div style={{ padding: 32, textAlign: "center", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 14, color: t.accent.red }}>
                ⚠️ {error}<br />
                <button onClick={fetchData} style={{ marginTop: 12, padding: "8px 16px", borderRadius: 8, border: `1px solid ${t.accent.red}44`, background: "none", color: t.accent.red, cursor: "pointer" }}>{s.retry}</button>
              </div>
            ) : view === "grid" ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 14 }}>
                {filteredItems.length === 0
                  ? <div style={{ gridColumn: "1/-1", textAlign: "center", color: t.text.placeholder, padding: "60px 0" }}>
                    {items.length === 0 ? (s.noMaterials) : (s.noMatch)}
                  </div>
                  : filteredItems.map((item, idx) => {
                    const st = stockStatus(item);
                    const sCfg = getStockStatusCfg(t)[st];
                    const pct = item.max_stock > 0 ? Math.min(100, (item.current_stock / item.max_stock) * 100) : 50;
                    const catColor = CATEGORY_COLORS[categories.indexOf(item.category) % CATEGORY_COLORS.length] || t.accent.blue;
                    const catIcon = CATEGORY_ICONS[item.category] || "📦";
                    const catBadge = CATEGORY_BADGE_COLORS[item.category] || { color: catColor, bg: catColor+"18" };
                    return (
                      <div key={item.id || idx} onClick={() => setSelected(item)} style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: 16, cursor: "pointer", transition: "all 0.2s", boxShadow: t.shadow.card, position: "relative", overflow: "hidden" }}
                        onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = `${sCfg.color}55`; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = t.border.card; }}>
                        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${sCfg.color}, transparent)`, borderRadius: "14px 14px 0 0" }} />
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary, marginBottom: 3 }}>{item.name}</div>
                            <div style={{ fontSize: 11, color: t.text.muted }}>{item.stock_code || "—"}</div>
                            <span style={{ fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:20, color:catBadge.color, background:catBadge.bg, marginTop:4, display:"inline-block" }}>{catIcon} {item.category||"—"}</span>
                          </div>
                          <StockBadge item={item} t={t} />
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                          <span style={{ fontSize: 11, color: t.text.muted, textTransform: "uppercase", fontWeight: 600 }}>{s.stockLevel}</span>
                          <span style={{ fontSize: 18, fontWeight: 800, color: sCfg.color }}>{item.current_stock} <span style={{ fontSize: 11, color: t.text.muted }}>{item.unit}</span></span>
                        </div>
                        <div style={{ height: 6, background: t.bg.tag, borderRadius: 3, overflow: "hidden", marginBottom: 12 }}>
                          <div style={{ height: "100%", width: `${pct}%`, borderRadius: 3, background: st === "ok" ? t.accent.green : st === "warning" ? t.accent.yellow : t.accent.red }} />
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: `${catColor}18`, color: catColor, fontWeight: 600 }}>{item.category || "—"}</span>
                          <span style={{ fontSize: 11, color: t.text.muted }}>{item.unit_price ? `${Number(item.unit_price).toLocaleString("tr-TR")} ₺` : "—"}</span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: t.bg.theadRow }}>
                      {[s.material, s.stockCode, s.category, s.currentStock, s.minStock, s.statusLabel, s.unitPrice, s.totalValue_, s.actions].map(h => (
                        <th key={h} style={{ padding: "12px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.length === 0
                      ? <tr><td colSpan={9} style={{ textAlign: "center", color: t.text.placeholder, padding: 40 }}>{s.noMaterialFound}</td></tr>
                      : filteredItems.map((item, i) => (
                        <tr key={item.id || i} style={{ borderTop: `1px solid ${t.border.subtle}`, background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                          <td style={{ padding: "12px 14px", fontWeight: 600, color: t.text.primary, fontSize: 13 }}>{item.name}</td>
                          <td style={{ padding: "12px 14px", fontSize: 12, color: t.text.muted }}>{item.stock_code || "—"}</td>
                          <td style={{ padding: "12px 14px" }}>
                            <span style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:20, color:(CATEGORY_BADGE_COLORS[item.category]||{color:t.text.muted}).color, background:(CATEGORY_BADGE_COLORS[item.category]||{bg:t.bg.tag}).bg }}>
                              {CATEGORY_ICONS[item.category]||"📦"} {item.category||"—"}
                            </span>
                          </td>
                          <td style={{ padding: "12px 14px", fontWeight: 700, fontSize: 13, color: getStockStatusCfg(t)[stockStatus(item)].color }}>{item.current_stock} {item.unit}</td>
                          <td style={{ padding: "12px 14px", fontSize: 12, color: t.text.muted }}>{item.min_stock || "—"}</td>
                          <td style={{ padding: "12px 14px" }}><StockBadge item={item} t={t} /></td>
                          <td style={{ padding: "12px 14px", fontSize: 13, color: t.text.secondary }}>{money(item.unit_price)}</td>
                          <td style={{ padding: "12px 14px", fontSize: 13, color: t.accent.blue, fontWeight: 600 }}>{money((item.current_stock || 0) * (item.unit_price || 0))}</td>
                          <td style={{ padding: "12px 14px" }}>
                            <div style={{ display: "flex", gap: 6 }}>
                              <button onClick={() => setSelected(item)} style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer", background: t.accent.blueBg, border: `1px solid ${t.accent.blueBorder}`, color: t.accent.blue }}>{s.detail}</button>
                              <button onClick={() => { setFormData(item); setFormType("stock"); }} style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer", background: t.bg.toggle, border: `1px solid ${t.border.input}`, color: t.text.secondary }}>✏️</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* BİTMİŞ ÜRÜN SEKMESİ */}
        {mainTab === "finished" && (
          <>
            <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
              {[
                { label: s.totalProducts,       value: finishedStats.total        || 0,   color: t.accent.blue,   icon: "📦" },
                { label: s.readyToShip_,   value: finishedStats.readyToShip  || 0,   color: t.accent.green,  icon: "✅" },
                { label: s.totalValue_,      value: finishedStats.totalValue ? `${(finishedStats.totalValue / 1000).toFixed(0)}K ₺` : "0 ₺", color: t.accent.purple, icon: "💰" },
              ].map(st => (
                <div key={st.label} style={{ flex: 1, minWidth: 160, background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: "16px 20px", display: "flex", alignItems: "center", gap: 14, boxShadow: t.shadow.card }}>
                  <span style={{ fontSize: 24 }}>{st.icon}</span>
                  <div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: st.color }}>{st.value}</div>
                    <div style={{ fontSize: 12, color: t.text.muted, fontWeight: 600 }}>{st.label}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ padding: "12px 20px", background: t.accent.blueBg, border: `1px solid ${t.accent.blueBorder}`, borderRadius: 12, marginBottom: 20, display: "flex", alignItems: "center", gap: 10, color: t.accent.blue, fontSize: 13 }}>
              ℹ️ {s.finishedStockInfo}
            </div>

            <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
              <input placeholder={`🔍 ${s.searchProduct}`} value={search} onChange={e => setSearch(e.target.value)} style={{ ...selectStyle, flex: 1, minWidth: 220 }} />
              <div style={{ display: "flex", gap: 4, background: t.bg.toggle, border: `1px solid ${t.border.input}`, borderRadius: 10, padding: 4 }}>
                {[["grid","⊞"],["list","≡"]].map(([v, icon]) => (
                  <button key={v} onClick={() => { setView(v); localStorage.setItem("view_stock", v); }} style={{ padding: "6px 12px", borderRadius: 7, border: "none", cursor: "pointer", background: view === v ? t.bg.toggleActive : "none", color: view === v ? t.text.primary : t.text.muted, fontSize: 16 }}>{icon}</button>
                ))}
              </div>
            </div>

            {loading ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 12 }}>
                <div style={{ width: 28, height: 28, border: `2px solid ${t.border.default}`, borderTopColor: t.accent.blue, borderRadius: "50%", animation: "sf-spin 0.8s linear infinite" }} />
                <span style={{ color: t.text.muted }}>{s.loading}</span>
              </div>
            ) : view === "grid" ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 14 }}>
                {finishedItems.filter(i => !search || (i.product_name||"").toLowerCase().includes(search.toLowerCase()) || (i.product_code||"").toLowerCase().includes(search.toLowerCase())).length === 0
                  ? <div style={{ gridColumn:"1/-1", textAlign:"center", color:t.text.placeholder, padding:"60px 0" }}>
                    {finishedItems.length === 0 ? (s.noFinishedGoods) : (s.noProductMatch)}
                  </div>
                  : finishedItems.filter(i => !search || (i.product_name||"").toLowerCase().includes(search.toLowerCase()) || (i.product_code||"").toLowerCase().includes(search.toLowerCase())).map((item, idx) => {
                    const { color: statusColor, label: statusLabel } = getFinishedStatusInfo(item);
                    const reservedQty  = Number(item.reserved_quantity) || 0;
                    const availableQty = Number(item.available_qty)     || 0;
                    const isPendingWarehouse = item.status === "pending_warehouse";
                    const isCustomerCancel   = item.source === "customer_cancel";
                    return (
                      <div key={item.id || idx} style={{ background: t.bg.card, border: `1px solid ${isCustomerCancel ? "rgba(251,191,36,0.4)" : t.border.card}`, borderRadius: 14, padding: 18, boxShadow: t.shadow.card, position: "relative", overflow: "hidden", transition: "all 0.2s" }}
                        onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = `${statusColor}55`; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = isCustomerCancel ? "rgba(251,191,36,0.4)" : t.border.card; }}>
                        <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:`linear-gradient(90deg, ${isCustomerCancel ? "#fbbf24" : statusColor}, transparent)`, borderRadius:"14px 14px 0 0" }} />
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:14 }}>
                          <div>
                            <div style={{ fontSize:14, fontWeight:700, color:t.text.primary, marginBottom:3 }}>{item.product_name || "—"}</div>
                            <div style={{ fontSize:11, color:t.text.muted }}>{item.product_code || "—"}</div>
                          </div>
                          <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 }}>
                            <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:20, color:statusColor, background:`${statusColor}18`, border:`1px solid ${statusColor}33`, textTransform:"uppercase", whiteSpace:"nowrap" }}>{statusLabel}</span>
                            {isCustomerCancel && <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:20, color:"#fbbf24", background:"rgba(251,191,36,0.12)", border:"1px solid rgba(251,191,36,0.3)", whiteSpace:"nowrap" }}>↩️ {s.customerCancel}</span>}
                          </div>
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:12 }}>
                          {[
                            [s.total_,        `${item.quantity || 0} ${item.unit||s.pcs}`, t.accent.green],
                            [s.reserved,       reservedQty > 0 ? `${reservedQty} ${item.unit||s.pcs}` : "—", reservedQty > 0 ? "#f59e0b" : t.text.muted],
                            [s.available,`${availableQty} ${item.unit||s.pcs}`, availableQty > 0 ? t.accent.green : t.accent.red],
                            [s.workOrder,       item.order_no || "—", t.text.secondary],
                          ].map(([label, value, color]) => (
                            <div key={label} style={{ background:t.bg.metricCard, borderRadius:8, padding:"8px 10px", border:`1px solid ${t.border.subtle}` }}>
                              <div style={{ fontSize:9, color:t.text.muted, textTransform:"uppercase", marginBottom:2 }}>{label}</div>
                              <div style={{ fontSize:13, fontWeight:700, color }}>{value}</div>
                            </div>
                          ))}
                          <div style={{ background:t.bg.metricCard, borderRadius:8, padding:"8px 10px", border:`1px solid ${t.border.subtle}`, gridColumn:"1/-1" }}>
                            <div style={{ fontSize:9, color:t.text.muted, textTransform:"uppercase", marginBottom:2 }}>{s.customer}</div>
                            <div style={{ fontSize:12, fontWeight:700, color:t.text.secondary }}>{item.customer || "—"}</div>
                          </div>
                        </div>
                        {reservedQty > 0 && (
                          <div style={{ padding:"6px 10px", background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.3)", borderRadius:8, marginBottom:10, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                            <span style={{ fontSize:11, color:"#f59e0b", fontWeight:600 }}>🔒 {reservedQty} {s.pcs} {s.reserved}</span>
                            <span style={{ fontSize:11, color: availableQty > 0 ? t.accent.green : t.accent.red, fontWeight:600 }}>✅ {availableQty} {s.available}</span>
                          </div>
                        )}
                        {isCustomerCancel && (
                          <div style={{ padding:"6px 10px", background:"rgba(251,191,36,0.08)", border:"1px solid rgba(251,191,36,0.25)", borderRadius:8, marginBottom:10, fontSize:11, color:"#fbbf24", fontWeight:600 }}>
                            ↩️ {s.customerCancelNote}
                          </div>
                        )}
                        {isPendingWarehouse && (
                          <button onClick={async (e) => {
                            e.stopPropagation();
                            const notes = prompt(s.warehouseAcceptNote);
                            if (notes === null) return;
                            try {
                              const res = await fetch(`${API}/finished-goods/${item.id}/warehouse-accept`, { method:"PATCH", headers:{ Authorization:`Bearer ${localStorage.getItem("token")}`, "Content-Type":"application/json" }, body:JSON.stringify({ notes: notes || "" }) });
                              if (res.ok) { alert(`✅ ${s.warehouseAccepted}`); fetchData(); }
                              else { const d = await res.json(); alert(`${s.error_}: ` + d.detail); }
                            } catch { alert(s.connectionError); }
                          }} style={{ width:"100%", padding:"7px 12px", borderRadius:8, border:"none", background:"#10b981", color:"#fff", cursor:"pointer", fontSize:11, fontWeight:700, marginBottom:8 }}>
                            📦 {s.acceptToWarehouse}
                          </button>
                        )}
                        <div style={{ display:"flex", justifyContent:"space-between", paddingTop:10, borderTop:`1px solid ${t.border.subtle}`, fontSize:11, color:t.text.muted }}>
                          <span>📍 {item.warehouse_location || (s.mainWarehouse)}</span>
                          <span>📅 {fmt(item.production_date || item.created_at)}</span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, overflow:"hidden", boxShadow:t.shadow.card }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead>
                    <tr style={{ background:t.bg.theadRow }}>
                      {[s.productName, s.productCode, s.total_, s.reserved, s.available, s.workOrder, s.customer, s.productionDate, s.statusLabel, s.source, s.actions].map(h => (
                        <th key={h} style={{ padding:"12px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", letterSpacing:"0.06em", whiteSpace:"nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {finishedItems.filter(i => !search || (i.product_name||"").toLowerCase().includes(search.toLowerCase()) || (i.product_code||"").toLowerCase().includes(search.toLowerCase())).length === 0
                      ? <tr><td colSpan={11} style={{ textAlign:"center", color:t.text.placeholder, padding:40 }}>{s.noProductFound}</td></tr>
                      : finishedItems.filter(i => !search || (i.product_name||"").toLowerCase().includes(search.toLowerCase()) || (i.product_code||"").toLowerCase().includes(search.toLowerCase())).map((item, i) => {
                        const { color: statusColor, label: statusLabel } = getFinishedStatusInfo(item);
                        const reservedQty  = Number(item.reserved_quantity) || 0;
                        const availableQty = Number(item.available_qty)     || 0;
                        const isPendingWarehouse = item.status === "pending_warehouse";
                        const isCustomerCancel   = item.source === "customer_cancel";
                        return (
                          <tr key={item.id || i} style={{ borderTop:`1px solid ${t.border.subtle}`, background: isCustomerCancel ? "rgba(251,191,36,0.04)" : i%2===0 ? t.bg.rowAlt : t.bg.row }}>
                            <td style={{ padding:"12px 14px", fontWeight:600, color:t.text.primary, fontSize:13 }}>{item.product_name}</td>
                            <td style={{ padding:"12px 14px", fontSize:12, color:t.text.muted }}>{item.product_code || "—"}</td>
                            <td style={{ padding:"12px 14px", fontWeight:700, color:t.accent.green, fontSize:13 }}>{item.quantity} {item.unit||s.pcs}</td>
                            <td style={{ padding:"12px 14px", fontSize:13, fontWeight:700, color: reservedQty > 0 ? "#f59e0b" : t.text.muted }}>{reservedQty > 0 ? `🔒 ${reservedQty}` : "—"}</td>
                            <td style={{ padding:"12px 14px", fontSize:13, fontWeight:700, color: availableQty > 0 ? t.accent.green : t.accent.red }}>{availableQty} {item.unit||s.pcs}</td>
                            <td style={{ padding:"12px 14px", fontSize:12, color:t.text.muted }}>{item.order_no || item.work_order_no || "—"}</td>
                            <td style={{ padding:"12px 14px", fontSize:12, color:t.text.muted }}>{item.customer || "—"}</td>
                            <td style={{ padding:"12px 14px", fontSize:12, color:t.text.muted }}>{fmt(item.production_date || item.created_at)}</td>
                            <td style={{ padding:"12px 14px" }}><span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:20, color:statusColor, background:`${statusColor}18`, border:`1px solid ${statusColor}33` }}>{statusLabel}</span></td>
                            <td style={{ padding:"12px 14px" }}>
                              {isCustomerCancel
                                ? <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:20, color:"#fbbf24", background:"rgba(251,191,36,0.12)", border:"1px solid rgba(251,191,36,0.3)", whiteSpace:"nowrap" }}>↩️ {s.customerCancel}</span>
                                : <span style={{ fontSize:11, color:t.text.muted }}>{s.normalProduction}</span>}
                            </td>
                            <td style={{ padding:"12px 14px" }}>
                              {isPendingWarehouse && ["admin","manager","warehouse","warehouse_chief"].includes(localStorage.getItem("role")) && (
                                <button onClick={async () => {
                                  const notes = prompt(s.warehouseAcceptNote);
                                  if (notes === null) return;
                                  try {
                                    const res = await fetch(`${API}/finished-goods/${item.id}/warehouse-accept`, { method:"PATCH", headers:{ Authorization:`Bearer ${localStorage.getItem("token")}`, "Content-Type":"application/json" }, body:JSON.stringify({ notes: notes || "" }) });
                                    if (res.ok) { alert(`✅ ${s.warehouseAccepted}`); fetchData(); }
                                    else { const d = await res.json(); alert(`${s.error_}: ` + d.detail); }
                                  } catch { alert(s.connectionError); }
                                }} style={{ padding:"6px 12px", borderRadius:8, border:"none", background:"#10b981", color:"#fff", cursor:"pointer", fontSize:11, fontWeight:700 }}>
                                  📦 {s.acceptToWarehouse}
                                </button>
                              )}
                              {item.status === "in_stock" && <span style={{ fontSize:11, color:t.accent.green }}>✅ {s.inStock}</span>}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* YARI MAMÜL SEKMESİ */}
        {mainTab === "semi" && (
          <>
            <div style={{ display:"flex", gap:12, marginBottom:24, flexWrap:"wrap" }}>
              {[
                { label: s.totalSemi, value: semiItems.length,                                          color: t.accent.purple, icon: "🔩" },
                { label: s.inStockSemi,            value: semiItems.filter(i=>i.status==="in_stock").length,          color: t.accent.green,  icon: "✅" },
                { label: s.reservedSemi,           value: semiItems.filter(i=>Number(i.reserved_quantity)>0).length,  color: "#f59e0b",        icon: "🔒" },
              ].map(st => (
                <div key={st.label} style={{ flex:1, minWidth:140, background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:"16px 20px", display:"flex", alignItems:"center", gap:14, boxShadow:t.shadow.card }}>
                  <span style={{ fontSize:24 }}>{st.icon}</span>
                  <div>
                    <div style={{ fontSize:22, fontWeight:800, color:st.color }}>{st.value}</div>
                    <div style={{ fontSize:12, color:t.text.muted, fontWeight:600 }}>{st.label}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding:"12px 20px", background:t.accent.blueBg, border:`1px solid ${t.accent.blueBorder}`, borderRadius:12, marginBottom:20, display:"flex", alignItems:"center", gap:10, color:t.accent.blue, fontSize:13 }}>
              🔩 {s.semiStockInfo}
            </div>
            <div style={{ display:"flex", gap:10, marginBottom:20 }}>
              <input placeholder={`🔍 ${s.searchSemi}`} value={search} onChange={e=>setSearch(e.target.value)} style={{ ...selectStyle, flex:1 }} />
            </div>
            <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, overflow:"hidden", boxShadow:t.shadow.card }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead>
                  <tr style={{ background:t.bg.theadRow }}>
                    {[s.productCode, s.productNameHeader, s.total_, s.available, s.source, s.date_, s.statusLabel].map(h => (
                      <th key={h} style={{ padding:"12px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", letterSpacing:"0.06em", whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {semiItems.filter(i => !search || (i.product_name||"").toLowerCase().includes(search.toLowerCase()) || (i.product_code||"").toLowerCase().includes(search.toLowerCase())).length === 0
                    ? <tr><td colSpan={7} style={{ textAlign:"center", color:t.text.placeholder, padding:40 }}>{s.noSemiGoods}</td></tr>
                    : semiItems.filter(i => !search || (i.product_name||"").toLowerCase().includes(search.toLowerCase()) || (i.product_code||"").toLowerCase().includes(search.toLowerCase())).map((item, i) => {
                      const availableQty = Number(item.quantity||0) - Number(item.reserved_quantity||0);
                      return (
                        <tr key={item.id||i} style={{ borderTop:`1px solid ${t.border.subtle}`, background:i%2===0?t.bg.rowAlt:t.bg.row }}>
                          <td style={{ padding:"12px 14px", fontSize:12, color:t.accent.blue, fontWeight:700 }}>{item.product_code||"—"}</td>
                          <td style={{ padding:"12px 14px", fontSize:13, fontWeight:600, color:t.text.primary }}>{item.product_name||"—"}</td>
                          <td style={{ padding:"12px 14px", fontSize:13, fontWeight:700, color:t.accent.green }}>{item.quantity||0} {item.unit||s.pcs}</td>
                          <td style={{ padding:"12px 14px", fontSize:13, fontWeight:700, color:availableQty>0?t.accent.green:t.accent.red }}>{availableQty} {item.unit||s.pcs}</td>
                          <td style={{ padding:"12px 14px", fontSize:12, color:t.text.muted }}>{item.source==="manual" ? s.manualSource : s.productionSource}</td>
                          <td style={{ padding:"12px 14px", fontSize:12, color:t.text.muted }}>{item.last_updated ? new Date(item.last_updated).toLocaleDateString(getLocale()) : "—"}</td>
                          <td style={{ padding:"12px 14px" }}>
                            <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:20, color:item.status==="in_stock"?t.accent.green:"#f59e0b", background:item.status==="in_stock"?t.accent.greenBg:t.accent.yellowBg }}>
                              {item.status==="in_stock" ? s.semiStatusInStock : s.semiStatusWaiting}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* TEDARİKÇİLER SEKMESİ */}
        {mainTab === "suppliers" && (
          <>
            <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
              <input placeholder={`🔍 ${s.searchSupplier}`} value={search} onChange={e => setSearch(e.target.value)} style={{ ...selectStyle, flex: 1 }} />
            </div>
            {filteredSuppliers.length === 0
              ? <div style={{ textAlign: "center", color: t.text.placeholder, padding: "60px 0" }}>
                {suppliers.length === 0 ? (s.noSuppliers) : (s.noSupplierMatch)}
              </div>
              : <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 14 }}>
                {filteredSuppliers.map((su, idx) => (
                  <SupplierCard key={su.id || idx} supplier={su} onClick={setSelectedSupplier} t={t} />
                ))}
              </div>}
          </>
        )}

        {mainTab === "requests" && (
          <StockRequests
            requests={requests} setRequests={setRequests}
            reqForm={reqForm} setReqForm={setReqForm}
            reqLoading={reqLoading} setReqLoading={setReqLoading}
            showReqForm={showReqForm} setShowReqForm={setShowReqForm}
            reqSuggestions={reqSuggestions} setReqSuggestions={setReqSuggestions}
            showReqSuggest={showReqSuggest} setShowReqSuggest={setShowReqSuggest}
            currentUser={currentUser} t={t} API={API} headers={headers}
          />
        )}
        {mainTab === "assembly" && (
          <AssemblyMaterialsTab t={t} API={API} headers={headers} currentUser={currentUser} />
        )}

        {!loading && !error && (
          <div style={{ marginTop: 16, color: t.text.placeholder, fontSize: 12, textAlign: "right" }}>
            {mainTab === "raw"       ? `${filteredItems.length} / ${items.length} ${s.materialCount}`
            : mainTab === "finished" ? `${finishedItems.length} ${s.finishedCount}`
            : mainTab === "semi"     ? `${semiItems.length} ${s.semiCount}`
            : mainTab === "suppliers"? `${filteredSuppliers.length} ${s.supplierCount_}`
            : `${requests.length} ${s.requestCount}`}
          </div>
        )}
      </div>

      {selected && (
        <StockDetailModal item={selected} t={t} themeName={themeName}
          onClose={() => setSelected(null)}
          onEdit={(i) => { setFormData(i); setFormType("stock"); setSelected(null); }} />
      )}
      {selectedSupplier && (
        <SupplierDetailModal supplier={selectedSupplier} t={t}
          onClose={() => setSelectedSupplier(null)}
          onEdit={(su) => { setFormData(su); setFormType("supplier"); setSelectedSupplier(null); }} />
      )}
      {formType && (
        <FormModal type={formType} data={formData} t={t}
          onClose={() => { setFormType(null); setFormData(null); }}
          onSaved={() => { setFormType(null); setFormData(null); fetchData(); }} />
      )}
    </div>
  );
}